<template>
  <div class="pro-yearly-landing">
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <span class="hero-badge">
            <i class="fas fa-crown mr-1" />{{ $t('pro-yearly.badge') }}
          </span>
          <h1 class="hero-title">
            {{ $t('pro-yearly.title') }}
          </h1>
          <p class="hero-subtitle">
            {{ $t('pro-yearly.subtitle') }}
          </p>

          <div class="price-row">
            <div class="price-box">
              <span class="price-currency">$</span>
              <span class="price-amount">49</span>
              <span class="price-period">/ {{ $t('general.year') }}</span>
            </div>
            <div class="price-comparison">
              <span class="strike">$120/{{ $t('general.year') }}</span>
              <span class="save-badge">{{ $t('pro-yearly.save-badge') }}</span>
            </div>
          </div>

          <button
            class="cta-button"
            :disabled="isLoading"
            @click="upgradeToPro"
          >
            <i
              v-if="isLoading"
              class="fas fa-spinner fa-spin mr-2"
            />
            <i
              v-else
              class="fas fa-bolt mr-2"
            />
            <span>{{ $t('pro-yearly.cta') }}</span>
            <span class="cta-time">{{ $t('pro-yearly.cta-time') }}</span>
          </button>

          <div
            v-if="error"
            class="error-banner"
          >
            <i class="fas fa-exclamation-circle" /> {{ error }}
          </div>

          <div class="trust-row">
            <div class="trust-item">
              <i class="fas fa-shield-alt" />
              {{ $t('pro-yearly.trust-secure') }}
            </div>
            <div class="trust-item">
              <i class="fas fa-undo" />
              {{ $t('pro-yearly.trust-refund') }}
            </div>
            <div class="trust-item">
              <i class="fas fa-lock" />
              {{ $t('pro-yearly.trust-cancel') }}
            </div>
          </div>

          <p
            v-if="!authStore.isAuthenticated"
            class="login-link"
          >
            {{ $t('auth.already-account') }}
            <router-link to="/auth/login?redirect=/upgrade">
              {{ $t('auth.sign-in') }}
            </router-link>
          </p>
        </div>
      </div>
    </section>

    <!-- Highlights -->
    <section class="highlights-section">
      <div class="container">
        <h2 class="section-title">
          {{ $t('pro-yearly.highlights-title') }}
        </h2>
        <div class="highlights-grid">
          <div class="highlight-card">
            <i class="fas fa-percentage" />
            <h3>{{ $t('pro-yearly.highlight-royalties-title') }}</h3>
            <p>{{ $t('pro-yearly.highlight-royalties-desc') }}</p>
          </div>
          <div class="highlight-card">
            <i class="fas fa-globe" />
            <h3>{{ $t('pro-yearly.highlight-stores-title') }}</h3>
            <p>{{ $t('pro-yearly.highlight-stores-desc') }}</p>
          </div>
          <div class="highlight-card">
            <i class="fas fa-bolt" />
            <h3>{{ $t('pro-yearly.highlight-fast-title') }}</h3>
            <p>{{ $t('pro-yearly.highlight-fast-desc') }}</p>
          </div>
          <div class="highlight-card">
            <i class="fas fa-tag" />
            <h3>{{ $t('pro-yearly.highlight-label-title') }}</h3>
            <p>{{ $t('pro-yearly.highlight-label-desc') }}</p>
          </div>
          <div class="highlight-card">
            <i class="fas fa-music" />
            <h3>{{ $t('pro-yearly.highlight-presaves-title') }}</h3>
            <p>{{ $t('pro-yearly.highlight-presaves-desc') }}</p>
          </div>
          <div class="highlight-card">
            <i class="fas fa-headset" />
            <h3>{{ $t('pro-yearly.highlight-support-title') }}</h3>
            <p>{{ $t('pro-yearly.highlight-support-desc') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Comparison strip -->
    <section class="comparison-section">
      <div class="container">
        <h2 class="section-title">
          {{ $t('pro-yearly.comparison-title') }}
        </h2>
        <div class="comparison-table-wrapper">
          <table class="comparison-table">
            <thead>
              <tr>
                <th>{{ $t('settings.feature') }}</th>
                <th>{{ $t('landing.pricing-tier1-name') }}</th>
                <th class="highlighted">
                  PRO
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ $t('royalties.royalty-split') }}</td>
                <td>85%</td>
                <td class="highlighted">
                  100%
                </td>
              </tr>
              <tr>
                <td>{{ $t('navigation.artists') }}</td>
                <td>1</td>
                <td class="highlighted">
                  5
                </td>
              </tr>
              <tr>
                <td>{{ $t('releases.monthly-limit') }}</td>
                <td>1</td>
                <td class="highlighted">
                  25
                </td>
              </tr>
              <tr>
                <td>{{ $t('royalties.min-payment') }}</td>
                <td>$100</td>
                <td class="highlighted">
                  $50
                </td>
              </tr>
              <tr>
                <td>{{ $t('settings.custom-label') }}</td>
                <td><i class="fas fa-times" /></td>
                <td class="highlighted">
                  <i class="fas fa-check" />
                </td>
              </tr>
              <tr>
                <td>{{ $t('settings.premium-stores') }}</td>
                <td><i class="fas fa-times" /></td>
                <td class="highlighted">
                  <i class="fas fa-check" />
                </td>
              </tr>
              <tr>
                <td>{{ $t('settings.presave') }}</td>
                <td><i class="fas fa-times" /></td>
                <td class="highlighted">
                  <i class="fas fa-check" />
                </td>
              </tr>
              <tr>
                <td>{{ $t('settings.faster-releases') }}</td>
                <td><i class="fas fa-times" /></td>
                <td class="highlighted">
                  <i class="fas fa-check" />
                </td>
              </tr>
              <tr>
                <td>{{ $t('settings.dedicated-support') }}</td>
                <td><i class="fas fa-times" /></td>
                <td class="highlighted">
                  <i class="fas fa-check" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="faq-section">
      <div class="container">
        <h2 class="section-title">
          {{ $t('landing.faq-title') }}
        </h2>
        <div class="faq-grid">
          <details
            v-for="(item, idx) in faqItems"
            :key="idx"
            class="faq-item"
          >
            <summary class="faq-question">
              {{ item.q }}
              <i class="fas fa-chevron-down" />
            </summary>
            <p class="faq-answer">
              {{ item.a }}
            </p>
          </details>
        </div>
      </div>
    </section>

    <!-- Final sticky CTA -->
    <section class="final-cta-section">
      <div class="container">
        <div class="final-cta">
          <h2>{{ $t('pro-yearly.final-cta-title') }}</h2>
          <button
            class="cta-button"
            :disabled="isLoading"
            @click="upgradeToPro"
          >
            <i
              v-if="isLoading"
              class="fas fa-spinner fa-spin mr-2"
            />
            <i
              v-else
              class="fas fa-rocket mr-2"
            />
            {{ $t('pro-yearly.cta') }}
          </button>
          <p class="final-cta-note">
            {{ $t('pro-yearly.final-cta-note') }}
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useHead } from '@vueuse/head';
import { useAuthStore } from '@/store/auth';
import userService from '@/services/userService';
import notificationService from '@/services/notificationService';
import { usePageSeo } from '@/composables/usePageSeo';
import { SITE_URL } from '@/config/seo';

const route = useRoute();
const { t } = useI18n();
const authStore = useAuthStore();

const stripe = ref(null);
const isLoading = ref(false);
const error = ref('');

const PRO_YEARLY_PRICE_ID = 'price_1LFOeXK9qKRAJ5zqfGt7fcx8';
const STRIPE_LIVE_KEY = 'pk_live_Fy0LR9SOyRoSrq4Byoj3mQtS00GjNUCMRm';
const STRIPE_TEST_KEY = 'pk_test_WD9FBh5jNgV5uO1eFQBkLdAl000Ey6vyno';

const proYearlyJsonLd = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: t('pro-yearly.seo.title'),
  description: t('pro-yearly.seo.description'),
  brand: {
    '@type': 'Brand',
    name: 'Indiefy',
  },
  offers: {
    '@type': 'Offer',
    priceCurrency: 'USD',
    price: '49',
    availability: 'https://schema.org/InStock',
    url: `${SITE_URL}/pro/yearly`,
  },
}));

usePageSeo({
  titleKey: 'pro-yearly.seo.title',
  descriptionKey: 'pro-yearly.seo.description',
  keywords: computed(() => t('pro-yearly.seo.keywords')),
  ogType: 'product',
  jsonLd: computed(() => [proYearlyJsonLd.value]),
});

// Extras outside the standard SEO schema (OG product pricing + Stripe preconnects).
useHead({
  meta: [
    { property: 'product:price:amount', content: '49' },
    { property: 'product:price:currency', content: 'USD' },
  ],
  link: [
    { rel: 'preconnect', href: 'https://checkout.stripe.com' },
    { rel: 'preconnect', href: 'https://js.stripe.com' },
  ],
});

const faqItems = computed(() => [
  { q: t('settings.faq-billing-question'), a: t('settings.faq-billing-answer') },
  { q: t('settings.faq-upgrade-question'), a: t('settings.faq-upgrade-answer') },
  { q: t('settings.faq-downgrade-question'), a: t('settings.faq-downgrade-answer') },
  { q: t('pro-yearly.faq-cancel-question'), a: t('pro-yearly.faq-cancel-answer') },
  { q: t('pro-yearly.faq-payment-question'), a: t('pro-yearly.faq-payment-answer') },
  { q: t('pro-yearly.faq-account-question'), a: t('pro-yearly.faq-account-answer') },
]);

const ensureStripe = () => {
  if (stripe.value) return stripe.value;
  if (!window.Stripe) {
    error.value = t('settings.stripe-not-loaded');
    return null;
  }
  const stripeKey = window.location.href.match(/localhost|cms-development/)
    ? STRIPE_TEST_KEY
    : STRIPE_LIVE_KEY;
  stripe.value = window.Stripe(stripeKey);
  return stripe.value;
};

const persistUtm = () => {
  ['utm_campaign', 'utm_source', 'utm_medium'].forEach((key) => {
    if (route.query[key] && window.localStorage) {
      localStorage.setItem(key, route.query[key]);
    }
  });
};

const upgradeToPro = async () => {
  error.value = '';
  const s = ensureStripe();
  if (!s) return;

  isLoading.value = true;

  const trackingData = {
    from: route.query.from || 'pro-yearly-landing',
  };
  if (window.localStorage) {
    trackingData.utm_campaign = localStorage.getItem('utm_campaign') || route.query.utm_campaign || '';
    trackingData.utm_source = localStorage.getItem('utm_source') || route.query.utm_source || '';
    trackingData.utm_medium = localStorage.getItem('utm_medium') || route.query.utm_medium || '';
  }

  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'AW-323371496/1fPACNGmjZ0YEOiDmZoB',
    });
  }

  try {
    if (authStore.isAuthenticated) {
      await userService.setSubscriptionTrackingData(trackingData).catch(() => {});
    }

    const checkoutOptions = {
      lineItems: [{ price: PRO_YEARLY_PRICE_ID, quantity: 1 }],
      mode: 'subscription',
      successUrl: `${window.location.origin}/pro/welcome?session_id={CHECKOUT_SESSION_ID}&from=pro-yearly`,
      cancelUrl: `${window.location.origin}/pro/yearly?cancelled=1`,
    };

    if (authStore.currentUser?.email) {
      checkoutOptions.customerEmail = authStore.currentUser.email;
    }

    const result = await s.redirectToCheckout(checkoutOptions);
    if (result.error) {
      error.value = result.error.message;
      isLoading.value = false;
    }
  } catch (e) {
    console.error('Stripe checkout error:', e);
    error.value = t('settings.upgrade-error');
    isLoading.value = false;
  }
};

onMounted(() => {
  persistUtm();

  if (route.query.cancelled) {
    notificationService.info({
      title: t('pro-yearly.cancelled-title'),
      message: t('pro-yearly.cancelled-message'),
    });
  }
});
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/typography";
@import "@/styles/abstracts/breakpoints";

.pro-yearly-landing {
  min-height: 100vh;
  background: var(--color-background, #ffffff);
}

.hero-section {
  position: relative;
  padding: 6rem 0 4rem;
  background: linear-gradient(135deg, $indiefy-dark-purple 0%, $indiefy-blue 60%, $indiefy-pink 100%);
  color: #ffffff;
  text-align: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 20%, rgba($indiefy-gold, 0.18) 0%, transparent 40%),
      radial-gradient(circle at 80% 80%, rgba($indiefy-pink, 0.18) 0%, transparent 40%);
    pointer-events: none;
  }

  > .container {
    position: relative;
    z-index: 1;
  }

  @media (max-width: $tablet) {
    padding: 4rem 0 3rem;
  }

  @media (max-width: $mobile) {
    padding: 3rem 0 2rem;
  }
}

.hero-content {
  max-width: 720px;
  margin: 0 auto;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  background: rgba($indiefy-gold, 0.18);
  color: $indiefy-gold;
  border: 1px solid $indiefy-gold;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
}

.hero-title {
  font-family: $family-display;
  font-size: clamp(1.85rem, 5vw, 4rem);
  font-weight: 800;
  line-height: 1.05;
  margin-bottom: 1rem;
  color: #ffffff;
  word-break: break-word;
}

.hero-subtitle {
  font-size: 1.25rem;
  opacity: 0.9;
  margin-bottom: 2.5rem;

  @media (max-width: $mobile) {
    font-size: 1.05rem;
    margin-bottom: 1.75rem;
  }
}

.price-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: $mobile) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
}

.price-box {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 18px;
  padding: 1.5rem 2rem;
  display: flex;
  align-items: baseline;
  gap: 0.25rem;

  .price-currency {
    font-size: 2rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
  }

  .price-amount {
    font-family: $family-display;
    font-size: 5rem;
    font-weight: 800;
    line-height: 1;
    color: #ffffff;
  }

  .price-period {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.85);
    margin-left: 0.5rem;
  }

  @media (max-width: $mobile) {
    padding: 1rem 1.5rem;
    border-radius: 14px;

    .price-currency {
      font-size: 1.5rem;
    }

    .price-amount {
      font-size: 3.5rem;
    }

    .price-period {
      font-size: 0.95rem;
      margin-left: 0.25rem;
    }
  }
}

.price-comparison {
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: $mobile) {
    text-align: center;
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
  }
}

.strike {
  text-decoration: line-through;
  color: rgba(255, 255, 255, 0.65);
  font-size: 1.1rem;
}

.save-badge {
  display: inline-block;
  background: $indiefy-gold;
  color: $indiefy-dark-purple;
  padding: 0.3rem 0.85rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, $indiefy-gold, $indiefy-pink);
  color: #ffffff;
  border: none;
  padding: 1.25rem 2.5rem;
  min-height: 56px;
  border-radius: 999px;
  font-size: 1.25rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 12px 32px rgba($indiefy-pink, 0.4);
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
  -webkit-tap-highlight-color: transparent;

  &:hover:not(:disabled) {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 16px 40px rgba($indiefy-pink, 0.5);
  }

  &:disabled {
    opacity: 0.85;
    cursor: wait;
  }

  .cta-time {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    opacity: 0.85;
    margin-left: 0.5rem;
  }

  @media (max-width: $mobile) {
    width: 100%;
    max-width: 360px;
    padding: 1rem 1.5rem;
    font-size: 1.1rem;
    flex-wrap: wrap;
    gap: 0.25rem;

    .cta-time {
      font-size: 0.7rem;
      margin-left: 0;
      flex-basis: 100%;
    }
  }
}

.error-banner {
  margin-top: 1rem;
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.trust-row {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 2rem;
  flex-wrap: wrap;
  opacity: 0.85;

  .trust-item {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;

    i {
      color: $indiefy-gold;
    }
  }

  @media (max-width: $mobile) {
    gap: 0.75rem 1.25rem;
    margin-top: 1.5rem;

    .trust-item {
      font-size: 0.8rem;
    }
  }
}

.login-link {
  margin-top: 2rem;
  font-size: 0.9rem;
  opacity: 0.85;

  a {
    color: $indiefy-gold;
    font-weight: 600;
    margin-left: 0.25rem;
  }
}

.section-title {
  font-family: $family-display;
  font-size: clamp(1.5rem, 3vw, 2.5rem);
  font-weight: 800;
  text-align: center;
  margin-bottom: 3rem;
  color: var(--color-text, #1a202c);
  word-break: break-word;

  @media (max-width: $mobile) {
    margin-bottom: 2rem;
  }
}

.highlights-section {
  padding: 5rem 0;

  @media (max-width: $mobile) {
    padding: 3rem 0;
  }
}

.highlights-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: $tablet) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: $mobile) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

.highlight-card {
  background: var(--color-background, #ffffff);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s;

  @media (max-width: $mobile) {
    padding: 1.5rem 1.25rem;
  }

  .body--dark & {
    background: #1e1e1e;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  &:hover {
    transform: translateY(-4px);
  }

  > i {
    font-size: 2.5rem;
    color: $indiefy-pink;
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.15rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: var(--color-text, #1a202c);
  }

  p {
    color: var(--color-text-light, #4a5568);
    line-height: 1.6;
  }
}

.comparison-section {
  padding: 4rem 0;
  background: var(--color-background-alt, #f9f9fb);

  .body--dark & {
    background: #141414;
  }

  @media (max-width: $mobile) {
    padding: 3rem 0;
  }
}

.comparison-table-wrapper {
  max-width: 720px;
  margin: 0 auto;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);

  .body--dark & {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: $mobile) {
    border-radius: 12px;
  }
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--color-background, #ffffff);

  .body--dark & {
    background: #1e1e1e;
  }

  th, td {
    padding: 1rem 1.25rem;
    text-align: center;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    .body--dark & {
      border-bottom-color: rgba(255, 255, 255, 0.06);
    }

    &:first-child {
      text-align: left;
      font-weight: 600;
    }

    &.highlighted {
      background: rgba($indiefy-pink, 0.06);
      color: $indiefy-pink;
      font-weight: 700;

      .body--dark & {
        background: rgba($indiefy-pink, 0.15);
      }
    }
  }

  thead th {
    background: var(--color-background-alt, #f5f5f5);

    .body--dark & {
      background: #2a2a2a;
    }

    &.highlighted {
      background: linear-gradient(135deg, $indiefy-pink, $indiefy-gold);
      color: #ffffff;
      font-size: 1.15rem;
    }
  }

  i.fa-check {
    color: $success;
  }

  i.fa-times {
    color: var(--color-text-light, #cbd5e0);
  }

  @media (max-width: $mobile) {
    th, td {
      padding: 0.75rem 0.65rem;
      font-size: 0.85rem;
    }

    thead th.highlighted {
      font-size: 1rem;
    }
  }

  @media (max-width: 380px) {
    th, td {
      padding: 0.65rem 0.4rem;
      font-size: 0.78rem;
    }
  }
}

.faq-section {
  padding: 5rem 0;

  @media (max-width: $mobile) {
    padding: 3rem 0;
  }
}

.faq-grid {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.faq-item {
  background: var(--color-background, #ffffff);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);

  .body--dark & {
    background: #1e1e1e;
  }

  &[open] .faq-question i {
    transform: rotate(180deg);
  }

  @media (max-width: $mobile) {
    padding: 0.875rem 1rem;
  }
}

.faq-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  list-style: none;
  color: var(--color-text, #1a202c);

  &::-webkit-details-marker {
    display: none;
  }

  i {
    color: $indiefy-blue;
    transition: transform 0.2s;
    flex-shrink: 0;
  }

  @media (max-width: $mobile) {
    font-size: 0.95rem;
  }
}

.faq-answer {
  padding-top: 0.75rem;
  color: var(--color-text-light, #4a5568);
  line-height: 1.6;
}

.final-cta-section {
  padding: 5rem 0;
  background: var(--color-background-alt, #f9f9fb);

  .body--dark & {
    background: #141414;
  }

  @media (max-width: $mobile) {
    padding: 3rem 0;
  }
}

.final-cta {
  background: linear-gradient(135deg, $indiefy-dark-purple, $indiefy-blue);
  border-radius: 24px;
  padding: 4rem 2rem;
  text-align: center;
  color: #ffffff;

  h2 {
    font-family: $family-display;
    font-size: clamp(1.5rem, 3vw, 2.5rem);
    font-weight: 800;
    margin-bottom: 2rem;
    color: #ffffff;
    word-break: break-word;
  }

  .final-cta-note {
    margin-top: 1rem;
    font-size: 0.9rem;
    opacity: 0.8;
  }

  @media (max-width: $mobile) {
    padding: 2.5rem 1.25rem;
    border-radius: 18px;

    h2 {
      margin-bottom: 1.5rem;
    }

    .final-cta-note {
      font-size: 0.8rem;
    }
  }
}
</style>
